<script>
import { defineComponent } from 'vue';
import { Event } from '@/models/Event';
import EventClassSelection from './EventClassSelection.vue';
import EventDescription from './EventDescription.vue';
import { globalParam } from '@/helpers/globals';
import getRulesMixin from '@/mixins/get-form-rules';
import StatusToggleBtns from '../StatusToggleBtns.vue';
import { userIsStaff } from '@/helpers/auth';
import EventOrganizerSelection from './EventOrganizerSelection.vue';
import LocationSelection from './LocationSelection.vue';
import UrlField from '@/components/form/UrlField.vue';
import TagSelect from '@/components/tag/select.vue';
import LabelField from '@/components/form/LabelField.vue';
import LocationEdit from './LocationEdit.vue';
import ScheduleEdit from './ScheduleEdit.vue';
import GenreGroupSelect from '@/components/genre_group/select.vue';
import ImagePanel from './ImagePanel.vue';
import { fieldRuleValue } from '@/helpers/tools';
import IconBtn from '@/components/IconBtn.vue';
import KpTextArea from '@/components/KpTextArea.vue';
import KpTextField from '@/components/KpTextField.vue';

export default defineComponent({
    name: 'QuickEditPanel',
    components: {
        EventClassSelection,
        EventDescription,
        StatusToggleBtns,
        EventOrganizerSelection,
        LocationSelection,
        UrlField,
        TagSelect,
        LocationEdit,
        ScheduleEdit,
        GenreGroupSelect,
        ImagePanel,
        LabelField,
        IconBtn,
        KpTextArea,
        KpTextField,
    },
    mixins: [getRulesMixin],
    props: {
        record: Event,
    },
    data() {
        return {
            toggleBtnsConf: {
                backgroundColor: 'transparent',
                iconColor: 'grey',
                iconActiveColor: 'white',
            },
            descriptionShort2Conf: {
                maxlength: 60,
            },
        };
    },
    created() {
        this.fieldRuleValue = fieldRuleValue;
    },
    mounted() {
        // activate first field
        this.$refs.focusField.$refs.textfield.focus();
    },

    methods: {
        globalParam,
        userIsStaff,
        onPropertyChange(change) {
            this.$emit('propertyChange', change);
        },
        onInput(fieldName, value) {
            this.onPropertyChange({ property: fieldName, value: value });
        },
        onLocationSaved(locRec, addressRec) {
            this.$emit('locationSaved', locRec, addressRec);
        },
        onScheduleSaved(scheduleRec) {
            this.$emit('scheduleSaved', scheduleRec);
        },
        onDeleteEvent() {
            this.$emit('eventDeleted', this.record);
        },
    },
});
</script>

<template>
    <div>
        <v-container>
            <v-row dense>
                <v-col cols="4">
                    <h3 class="section-heading">{{ $t('veranstaltungsart') }}</h3>
                    <EventClassSelection :record="record" @propertyChange="onPropertyChange" />
                    <h3 class="section-heading d-flex">
                        <span class="flex-grow-1">{{ $t('status_and_infos') }}</span>
                        <span v-if="record.bp_import_origin" class="font-weight-regular">
                            {{ $t('quelle') }}: {{ record.bp_import_origin }}
                        </span>
                    </h3>

                    <div class="d-flex">
                        <StatusToggleBtns
                            :value="record.status"
                            :background-color="toggleBtnsConf.backgroundColor"
                            :icon-color="toggleBtnsConf.iconColor"
                            :icon-active-color="toggleBtnsConf.iconActiveColor"
                            hide-deleted
                            @change="onInput('status', $event)"
                        />
                        <v-btn-toggle
                            v-if="userIsStaff()"
                            :value="record?.import_protection ? 'protected' : ''"
                            borderless
                            tile
                            :background-color="toggleBtnsConf.backgroundColor"
                            @change="onInput('import_protection', !record.import_protection)"
                        >
                            <IconBtn
                                value="protected"
                                :icon-color="toggleBtnsConf.iconColor"
                                :icon-active-color="toggleBtnsConf.iconActiveColor"
                                icon-disabled-color="white"
                                icon-cls="importsafe"
                                margin-class=""
                                :tooltip="$t('import_protection')"
                                color="primary"
                            />
                        </v-btn-toggle>
                    </div>

                    <LabelField :label="$t('title')">
                        <KpTextField
                            ref="focusField"
                            autofocus
                            :value="record.title"
                            :placeholder="$t('placeholder_event_info_categories_title')"
                            dense
                            outlined
                            hide-details="auto"
                            clearable
                            :disabled="!record.canEditTitle"
                            :counter="globalParam('event.max_title_chars', 70)"
                            :rules="[
                                getRule('required'),
                                getRule('maxLength')(globalParam('event.max_title_chars', 70)),
                            ]"
                            :max-length="globalParam('event.max_title_chars', 70)"
                            @change="onInput('title', $event)"
                        />
                    </LabelField>

                    <div class="d-flex gap-0 mb-2">
                        <v-checkbox
                            class="radio-equal"
                            :value="record.for_print"
                            :true-value="true"
                            :false-value="false"
                            :input-value="record.for_print"
                            dense
                            hide-details="auto"
                            :label="$t('print')"
                            :ripple="false"
                            @change="onInput('for_print', $event)"
                        />
                        <v-checkbox
                            class="radio-equal"
                            :value="record.for_website"
                            :true-value="true"
                            :false-value="false"
                            :input-value="record.for_website"
                            dense
                            hide-details="auto"
                            :label="$t('website')"
                            :ripple="false"
                            @change="onInput('for_website', $event)"
                        />
                        <v-checkbox
                            class="radio-equal"
                            :value="record.is_featured"
                            :true-value="true"
                            :false-value="false"
                            :input-value="record.is_featured"
                            dense
                            hide-details="auto"
                            :label="$t('tip')"
                            :ripple="false"
                            @change="onInput('is_featured', $event)"
                        />
                        <v-checkbox
                            class="radio-equal"
                            :value="record.edited"
                            :true-value="true"
                            :false-value="false"
                            :input-value="record.edited"
                            dense
                            hide-details="auto"
                            :label="$t('redigiert')"
                            :ripple="false"
                            @change="onInput('edited', $event)"
                        />
                    </div>

                    <EventOrganizerSelection :record="record" @input="onInput('organizer_id', $event)" />

                    <LocationSelection :record="record" @input="onInput('location_id', $event)" />

                    <LabelField class="mb-2" :label="$t('online_event')" :label-tip="$t('online_event_help')">
                        <UrlField
                            :value="record.online_event_url"
                            :placeholder="$t('placeholder_event_info_categories_online_event_url')"
                            @change="onInput('online_event_url', $event)"
                        />
                    </LabelField>

                    <LabelField class="mb-2" :label="$t('hauptsparten')">
                        <GenreGroupSelect
                            :value="record.genre_groups"
                            :page-id="1"
                            :placeholder="$t('placeholder_event_info_categories_genre_groups')"
                            clearable
                            :disabled="!record.canEditGenreGroups"
                            :rules="getRules('required')"
                            multiple
                            chips
                            deletable-chips
                            small-chips
                            hide-details="auto"
                            @change="onInput('genre_groups', $event)"
                        />
                    </LabelField>

                    <LabelField class="mb-2" :label="$t('frontsparte')" :label-tip="$t('frontsparte_tip')">
                        <GenreGroupSelect
                            :value="record.main_genre_group_id"
                            :page-id="1"
                            :placeholder="$t('placeholder_event_info_categories_main_genre_group_id')"
                            :rules="getRules('required')"
                            :allow-only="record.genre_groups || []"
                            :disabled="!record.canEditMainGenreGroup"
                            clearable
                            hide-details="auto"
                            @change="onInput('main_genre_group_id', $event)"
                        />
                    </LabelField>

                    <LabelField class="mb-2" :label="$t('description_short')" :label-tip="$t('description_short_tip')">
                        <KpTextArea
                            :value="record.summary_text"
                            dense
                            outlined
                            hide-details="auto"
                            :counter="globalParam('event.max_summary_text_chars', 200)"
                            :rules="[getRule('eventSummaryText')]"
                            :placeholder="$t('event_description_short_placeholder')"
                            :max-length="globalParam('event.max_summary_text_chars', 200)"
                            @change="onInput('summary_text', $event)"
                        />
                    </LabelField>

                    <LabelField v-if="userIsStaff()" class="mb-2" :label="$t('description_short_2')">
                        <KpTextArea
                            :value="record.abstract"
                            dense
                            outlined
                            rows="2"
                            hide-details="auto"
                            :counter="descriptionShort2Conf.maxlength"
                            :disabled="!record.canEditAbstract"
                            :rules="[getRule('maxLength')(descriptionShort2Conf.maxlength)]"
                            :max-length="descriptionShort2Conf.maxlength"
                            @change="onInput('abstract', $event)"
                        />
                    </LabelField>

                    <LabelField
                        class="mb-2"
                        :label="$t('interessen_tags_erweiterte_suche')"
                        :label-tip="$t('interessen_tags_erweiterte_suche_tip')"
                    >
                        <TagSelect
                            :value="record.tags"
                            item-value="id"
                            for-event
                            :placeholder="$t('placeholder_event_description_tags')"
                            :show-add="false"
                            :show-download="false"
                            clearable
                            hide-details="auto"
                            @input="onInput('tags', $event)"
                        />
                    </LabelField>

                    <ImagePanel class="mb-2" :event-record="record" @propertyChange="onPropertyChange" />
                </v-col>
                <v-col cols="8">
                    <ScheduleEdit :event="record" @saved="onScheduleSaved" />
                    <EventDescription :record="record" class="mb-2" @propertyChange="onPropertyChange" />
                    <LocationEdit :event-record="record" @saved="onLocationSaved" />
                </v-col>
            </v-row>
            <v-row justify="end">
                <v-col cols="1">
                    <v-btn color="secondary" @click="onDeleteEvent">
                        {{ $t('delete_event') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<style lang="scss" scoped>
.radio-equal {
    flex-grow: 1;
    width: 49%;
    margin-right: 0 !important;
}
</style>
